import { InfoCircleFilled } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select, Space, Switch, Tooltip, Typography } from "antd";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import { ScheduleRate } from "common/enums";
import BusinessAreaSelectFormItem from "containers/BusinessAreaSelectFormItem";
import DataSetSelectFormItem from "containers/DataSetSelectFormItem";
import DataSyndicationSourceSubscription from "containers/DataSyndicationSourceSubscription";
import SchemaModelFormItem from "containers/SchemaModelFormItem";
import dayjs from "dayjs";
import { diff } from "deep-object-diff";
import _ from "lodash";
import { useCallback, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;

const DATASET_DEFAULT_ITEM = {
    dataSetId: "ALL_DATASETS",
    dataSetName: "All Datasets under this schema",
};

const EMPTY_OBJECT = {};

function ManageDataSyndicationSource({ action, syndicationSourceVaue, formErrors, onSave, onCancel, permission, readOnlyView, onValuesChanged }) {
    const [selectedBusinessAreaId, setSelectedBusinessAreaId] = useState((syndicationSourceVaue && syndicationSourceVaue.businessAreaId) || null);
    const [selectedSchemaId, setSelectedSchemaId] = useState((syndicationSourceVaue && syndicationSourceVaue.schemaId) || null);
    const [shareDataSetChecked, setShareDataSetChecked] = useState((syndicationSourceVaue && syndicationSourceVaue.enabled) || false);
    const [form] = Form.useForm();

    const onBusinessAreaChange = useCallback(
        (businessAreaId, businessArea) => {
            setSelectedBusinessAreaId(businessAreaId);
            if (form.getFieldValue("businessAreaId") !== businessAreaId) {
                form.setFieldsValue({
                    businessAreaDescription: businessArea.description,
                    schemaId: null,
                    dataSetId: DATASET_DEFAULT_ITEM.dataSetId,
                });
            } else {
                form.setFieldsValue({
                    businessAreaDescription: businessArea.description,
                });
            }
        },
        [form],
    );

    const onSchemaModelChange = useCallback(
        (schemaId, schemaModel) => {
            setSelectedSchemaId(schemaId);
            if (form.getFieldValue("schemaId") !== schemaId) {
                form.setFieldsValue({
                    schemaDescription: schemaModel.description,
                    dataSetId: DATASET_DEFAULT_ITEM.dataSetId,
                });
            } else {
                form.setFieldsValue({
                    schemaDescription: schemaModel.description,
                });
            }
        },
        [form],
    );

    const onDataSetChange = useCallback((dataSetId, dataSet) => {}, []);

    const onShareDataSetChange = useCallback((e) => {
        setShareDataSetChecked(e.target.checked);
    }, []);

    const getFormErrors = useCallback(
        (fieldName) => {
            if (formErrors && formErrors[fieldName]) {
                return {
                    help: formErrors[fieldName],
                    validateStatus: "error",
                };
            }
            return EMPTY_OBJECT;
        },
        [formErrors],
    );

    const onFormSubmit = useCallback(
        (formData) => {
            let syndicationSource = { ...formData };
            if (action === "create") {
                onSave(syndicationSource);
            } else {
                let diffObject = diff(syndicationSourceVaue, syndicationSource);
                let updatedObject = {};
                for (let key in diffObject) {
                    if (diffObject[key] !== undefined) {
                        updatedObject[key] = syndicationSource[key];
                    }
                }
                if (_.isEmpty(updatedObject) === false) {
                    updatedObject.version = syndicationSourceVaue.version;
                }

                onSave(syndicationSourceVaue.sourceId, updatedObject);
            }
        },
        [action, syndicationSourceVaue, onSave],
    );

    const isReadOnlyView = useCallback(() => {
        return readOnlyView || !(permission.canEdit || permission.canAdd);
    }, [readOnlyView, permission]);

    const isSaveAllowed = useCallback(() => {
        if (action === "edit") {
            return permission.canEdit;
        } else if (action === "create") {
            return permission.canAdd;
        }
        return false;
    }, [action, permission]);

    const getDefaultValidFrom = useCallback(() => {
        return dayjs().add(15, "minute");
    }, []);

    const validateValidFromDate = (_, value) => {
        if (!value || dayjs(value).isAfter(dayjs().add(15, "minute"))) {
            return Promise.resolve();
        }
        return Promise.reject(new Error("Valid From must be at least 15 minutes from now!"));
    };

    const validateValidUntilDate = (_, value) => {
        const validFromDate = form.getFieldValue("validFrom");
        if (!value || (validFromDate && dayjs(value).isAfter(validFromDate))) {
            return Promise.resolve();
        }
        return Promise.reject(new Error("Valid Until must be after the Valid From!"));
    };

    return (
        <div style={{ height: "100%", overflow: "auto", padding: "5px", position: "relative" }}>
            <Form
                name="basic"
                form={form}
                layout="vertical"
                labelAlign="right"
                labelCol={{ md: { span: 24 }, lg: { span: 6 }, xl: { span: 5 }, xxl: { span: 3 } }}
                style={{ maxWidth: "100%" }}
                initialValues={
                    syndicationSourceVaue || {
                        dataSetId: DATASET_DEFAULT_ITEM.dataSetId,
                        validFrom: action === "create" ? getDefaultValidFrom() : undefined,
                    }
                }
                onFinish={onFormSubmit}
                onValuesChange={onValuesChanged}
                disabled={isReadOnlyView()}
            >
                <Card
                    variant="outlined"
                    style={{ marginBottom: 24 }}
                    title={
                        <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            Details
                        </Typography.Title>
                    }
                >
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} name="name" label="Name" rules={nameRules} {...getFormErrors("name")}>
                                <Input data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_NAME_INPUT} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} name="description" label="Purpose" rules={descriptionRules} {...getFormErrors("description")}>
                                <Input data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_PURPOSE_INPUT} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <BusinessAreaSelectFormItem
                                form={form}
                                formItemProps={{
                                    labelCol: { span: 24 },
                                    label: "Business Area",
                                    name: "businessAreaId",
                                    rules: [{ required: true, message: "Please select a Business Area" }],
                                    ...getFormErrors("businessAreaId"),
                                }}
                                disabled={isReadOnlyView()}
                                onChange={onBusinessAreaChange}
                            ></BusinessAreaSelectFormItem>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} name="businessAreaDescription" label="Business Area Description">
                                <Input readOnly={true} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_BUSINESS_AREA_DESC_INPUT} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <SchemaModelFormItem
                                form={form}
                                businessAreaId={selectedBusinessAreaId}
                                formItemProps={{
                                    labelCol: { span: 24 },
                                    label: "Schema",
                                    name: ["schemaId"],
                                    rules: [{ required: true, message: "Please select a Schema" }],
                                    ...getFormErrors("schemaId"),
                                }}
                                disabled={isReadOnlyView()}
                                onChange={onSchemaModelChange}
                            ></SchemaModelFormItem>
                        </Col>
                        <Col span={12}>
                            <Form.Item labelCol={{ span: 24 }} name="schemaDescription" label="Schema Description">
                                <Input readOnly={true} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_SCHEMA_DESC_INPUT} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Row>
                                <Col span={4}>
                                    <Form.Item name="shareDataset" valuePropName="checked" {...getFormErrors("shareDataset")}>
                                        <Checkbox onChange={onShareDataSetChange} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_SHARE_DATASET_CHECKBOX}>
                                            Share Dataset
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                                <Col span={20}>
                                    <DataSetSelectFormItem
                                        businessAreaId={selectedBusinessAreaId}
                                        schemaId={selectedSchemaId}
                                        formItemProps={{
                                            name: "dataSetId",
                                            ...getFormErrors("dataSetId"),
                                        }}
                                        disabled={isReadOnlyView() || !shareDataSetChecked}
                                        onChange={onDataSetChange}
                                        defaultItem={DATASET_DEFAULT_ITEM}
                                    ></DataSetSelectFormItem>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Row gutter={24}>
                                <Col span={12}>
                                    <Form.Item
                                        labelCol={{ span: 24 }}
                                        label="Scheduling Interval"
                                        name="rateValue"
                                        rules={[{ required: true, message: "Scheduling Interval is required" }]}
                                        {...getFormErrors("rateValue")}
                                    >
                                        <InputNumber min={1} style={{ width: "100%" }} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_SCHEDULING_INTERVAL_INPUT} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        labelCol={{ span: 24 }}
                                        label="Scheduling Unit"
                                        name="rateUnit"
                                        rules={[{ required: true, message: "Scheduling Unit is required" }]}
                                        {...getFormErrors("rateUnit")}
                                    >
                                        <Select data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_SCHEDULING_UNIT_SELECT}>
                                            {Object.keys(ScheduleRate).map((rate) => (
                                                <Option key={rate} value={rate}>
                                                    {ScheduleRate[rate]}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Row gutter={24}>
                                <Col span={12}>
                                    <Form.Item
                                        labelCol={{ span: 24 }}
                                        label={
                                            <span>
                                                Valid From{" "}
                                                <Tooltip title="Start time must be at least 15 minutes from now. Defaults to 15 minutes from current time.">
                                                    <InfoCircleFilled style={{ color: "#1890ff" }} />
                                                </Tooltip>
                                            </span>
                                        }
                                        name="validFrom"
                                        {...getFormErrors("validFrom")}
                                        rules={[{ validator: validateValidFromDate }]}
                                    >
                                        <DatePicker showTime style={{ width: "100%" }} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_VALID_FROM_CALENDAR} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        labelCol={{ span: 24 }}
                                        label="Valid Until"
                                        name="validUntil"
                                        {...getFormErrors("validUntil")}
                                        rules={[{ validator: validateValidUntilDate }]}
                                    >
                                        <DatePicker showTime style={{ width: "100%" }} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_VALID_UNTIL_CALENDAR} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item name="enabled" label="Enabled" valuePropName="checked" {...getFormErrors("enabled")}>
                                <Switch data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_ENABLED_SWITCH} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider style={{ margin: "24px 0" }} />
                    <Row>
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Space>
                                <Button onClick={onCancel} disabled={false} data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_CANCEL_BUTTON}>
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={isReadOnlyView() || !isSaveAllowed()}
                                    data-testid={CypressTestIds.SYNDICATION_SOURCE_MANAGE_SAVE_BUTTON}
                                >
                                    Save
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Form>
            {action !== "create" && (
                <>
                    <Divider />
                    <DataSyndicationSourceSubscription readOnly={isReadOnlyView()} sourceId={syndicationSourceVaue.sourceId} />
                </>
            )}
        </div>
    );
}

export default ManageDataSyndicationSource;
